import { timepointLabel } from '../../api/know'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  admin,
  type AdminInvitation,
  type AdminQuestionnaireSummary,
} from '../../api/admin'
import { ApiError } from '../../api/client'
import { InviteModal } from './InviteModal'

/**
 * /admin/invitations — lista todas las invitaciones a cuestionarios.
 * Permite filtrar por code/status, copiar el link, reenviar mail (si SMTP
 * está configurado) y revocar.
 *
 * El modal `InviteModal` se reutiliza desde acá para crear nuevas. Cada
 * mutación recarga la lista.
 */
export function AdminInvitations() {
  const [params, setParams] = useSearchParams()
  const code = params.get('code') ?? ''
  const status = params.get('status') ?? ''
  const [items, setItems] = useState<AdminInvitation[] | null>(null)
  const [catalog, setCatalog] = useState<AdminQuestionnaireSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [copiedId, setCopiedId] = useState<number | null>(null)

  async function reload() {
    setError(null)
    try {
      const r = await admin.invitations({
        code: code || undefined,
        status: status || undefined,
      })
      setItems(r.items)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }

  useEffect(() => {
    admin.questionnaires()
      .then((r) => setCatalog(r.items))
      .catch(() => {})
  }, [])

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, status])

  function setParam(name: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(name, value)
    else next.delete(name)
    setParams(next)
  }

  async function copyLink(inv: AdminInvitation) {
    await navigator.clipboard.writeText(inv.link_url)
    setCopiedId(inv.id)
    window.setTimeout(() => setCopiedId(null), 1500)
  }

  async function resend(id: number) {
    setBusyId(id)
    try {
      const r = await admin.resendInvitation(id)
      if (!r.email_sent) {
        setError(
          'No se pudo mandar el mail. ¿SMTP configurado? Copiá el link a mano.',
        )
      }
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusyId(null)
    }
  }

  async function revoke(id: number) {
    if (!window.confirm('¿Revocar esta invitación? El link deja de servir.')) return
    setBusyId(id)
    try {
      await admin.revokeInvitation(id)
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section>
      <div className="page-header">
        <h1>Invitaciones</h1>
        <div className="flex items-center gap-sm">
          <Link to="/admin/questionnaires" className="btn no-underline">
            ← Cuestionarios
          </Link>
          {catalog && catalog.length > 0 && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowCreate(true)}
            >
              + Invitar
            </button>
          )}
        </div>
      </div>

      <p className="mb-md text-sm text-text-muted">
        Linkear a un destinatario por mail para que complete un cuestionario.
        Si SMTP no está configurado en el backend, el mail no se manda — el
        admin copia el link y lo pega donde quiera (WhatsApp, etc.).
      </p>

      <div className="filter-bar">
        <select
          className="input max-w-[220px]"
          value={code}
          onChange={(e) => setParam('code', e.target.value)}
        >
          <option value="">Todos los cuestionarios</option>
          {catalog?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} — {c.name_es}
            </option>
          ))}
        </select>
        <select
          className="input max-w-[200px]"
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="scheduled">Programadas</option>
          <option value="used">Usadas</option>
          <option value="expired">Vencidas</option>
          <option value="revoked">Revocadas</option>
        </select>
      </div>

      {error && <div className="banner-err mb-md">{error}</div>}

      {showCreate && catalog && catalog.length > 0 && (
        <InviteModal
          defaultCode={code || catalog[0]?.code || ''}
          codes={catalog.map((c) => ({ code: c.code, name_es: c.name_es }))}
          onClose={() => {
            setShowCreate(false)
            void reload()
          }}
        />
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Cuestionario</th>
              <th>Momento</th>
              <th>Estado</th>
              <th>Mail</th>
              <th>Creada</th>
              <th>Vence</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items?.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.email}</td>
                <td>
                  <span className="tag">{inv.questionnaire_code}</span>
                </td>
                <td>
                  {inv.timepoint ? (
                    <span className="tag normal-case tracking-normal">
                      {timepointLabel(inv.timepoint)}
                    </span>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
                <td>
                  <StatusBadge inv={inv} />
                </td>
                <td className="text-text-muted">
                  {inv.email_sent_at
                    ? `Mandado ${new Date(inv.email_sent_at).toLocaleDateString('es-AR')}`
                    : 'Sin mail'}
                </td>
                <td className="text-text-muted">
                  {new Date(inv.created_at).toLocaleDateString('es-AR')}
                </td>
                <td className="text-text-muted">
                  {new Date(inv.expires_at).toLocaleDateString('es-AR')}
                </td>
                <td className="text-right">
                  <div className="flex justify-end gap-xs">
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => void copyLink(inv)}
                    >
                      {copiedId === inv.id ? 'Copiado ✓' : 'Copiar link'}
                    </button>
                    {inv.status === 'pending' && (
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        disabled={busyId === inv.id}
                        onClick={() => void resend(inv.id)}
                      >
                        Reenviar mail
                      </button>
                    )}
                    {(inv.status === 'pending' || inv.status === 'scheduled') && (
                      <button
                        type="button"
                        className="btn-ghost text-xs text-danger"
                        disabled={busyId === inv.id}
                        onClick={() => void revoke(inv.id)}
                      >
                        Revocar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items === null && (
              <tr>
                <td colSpan={8} className="text-text-muted">
                  Cargando…
                </td>
              </tr>
            )}
            {items?.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-sm text-text-muted">
                  Sin invitaciones con esos filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function StatusBadge({ inv }: { inv: AdminInvitation }) {
  const map: Record<AdminInvitation['status'], { label: string; cls: string }> = {
    pending: { label: 'Pendiente', cls: 'tag-ok' },
    scheduled: {
      label: inv.available_from
        ? `Programada · ${new Date(inv.available_from).toLocaleDateString('es-AR')}`
        : 'Programada',
      cls: 'tag',
    },
    used: { label: 'Usada', cls: 'tag' },
    expired: { label: 'Vencida', cls: 'tag-err' },
    revoked: { label: 'Revocada', cls: 'tag-err' },
  }
  const m = map[inv.status] ?? { label: inv.status, cls: 'tag' }
  return <span className={`${m.cls} normal-case tracking-normal`}>{m.label}</span>
}
